import type { DisputeResolution, EvidenceMediaType, EvidenceScanState, LineDisputeState, PartyType, StatementDispute } from "../../../api/types";
import type { Tone } from "../../../components/ui";

// Wording for statement-line disputes (UX-DESIGN.md §2.2 billing
// disputes, §3.6 publisher disputes; migration 0063). Machine values are
// 0063's; the words are the portal's.

/** Evidence limits, mirrored from the server so a file is refused before it's sent. */
export const EVIDENCE_MAX_BYTES = 2 * 1024 * 1024; // store_dispute_evidence c_max_bytes, 0063:534; dispute_evidence.go:52
export const EVIDENCE_PARTY_CAP_BYTES = 20 * 1024 * 1024; // c_party_cap, 0063:535
export const EVIDENCE_MAX_ITEMS = 10; // file_statement_line_dispute c_max_evidence, 0063:746
export const REASON_MAX_CHARS = 2000; // invalid_reason {min_length 1, max_length 2000}, 0063:773-774
export const EVIDENCE_TYPES: EvidenceMediaType[] = ["application/pdf", "image/png", "image/jpeg", "text/plain"]; // 0063:536
export const EVIDENCE_ACCEPT = ".pdf,.png,.jpg,.jpeg,.txt,application/pdf,image/png,image/jpeg,text/plain";
export const EVIDENCE_LIMITS_TEXT =
  "PDF, PNG, JPEG or plain text. Up to 2 MB a file and 10 files a dispute; your account can store 20 MB of evidence in all. Every file is checked before a reviewer can open it, and only files that pass can be attached.";
/** dispute_evidence.go:17-20: the scanner in this build is a stub, stated as one. */
export const STUB_SCANNER_TEXT =
  "On this deployment the check confirms the file really is the type it claims and refuses PDFs with active content. It isn't a full malware scan.";

export const MEDIA_LABEL: Record<EvidenceMediaType, string> = {
  "application/pdf": "PDF",
  "image/png": "PNG image",
  "image/jpeg": "JPEG image",
  "text/plain": "Text",
};

export const SCAN_STATE: Record<EvidenceScanState, { label: string; tone: Tone }> = {
  quarantined: { label: "Being checked", tone: "info" },
  clean: { label: "Passed check", tone: "success" },
  rejected: { label: "Failed check", tone: "error" },
};

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 102.4) / 10} KB`;
  return `${Math.round(n / 104857.6) / 10} MB`;
}

export const LINE_STATE: Record<LineDisputeState, { label: string; tone: Tone }> = {
  eligible: { label: "Can be disputed", tone: "neutral" },
  window_closed: { label: "Window closed", tone: "neutral" },
  not_disputable: { label: "Not disputable", tone: "neutral" },
  open: { label: "Dispute open", tone: "warning" },
  resolved: { label: "Dispute resolved", tone: "info" },
};

/** An amendment's reason (statement_amendment_reasons, 0063:1318-1331; rules 0063:1207-1313). */
export function amendmentReasonText(reason: string): string {
  switch (reason) {
    case "statement_dispute_upheld":
      return "A dispute on this statement was upheld";
    case "payout_failed":
      return "A payout failed and its funds came back";
    case "payout_transfer_reversed":
      return "A payout transfer was reversed";
    case "payout_reversal_restored":
      return "A reversed payout was restored";
    case "chargeback_reversed":
      return "A chargeback was reversed";
    case "refund_failed":
    case "refund_failed_after_confirmation":
      return "A refund failed and its funds came back";
    case "debt_case_restored":
      return "A debt case was resolved and earnings restored";
    case "debt_case_appeal_upheld":
      return "A debt-case appeal was upheld";
    case "provider_double_payment":
      return "A duplicate payment by the payment provider was corrected";
    case "late_ledger_entry":
      return "A ledger entry for this period was recorded after the statement was issued";
    case "multiple":
      return "Several corrections";
    default:
      return reason.replace(/_/g, " ");
  }
}

export function disputesPath(home: string, id?: string): string {
  return id ? `${home}/disputes/${id}` : `${home}/disputes`;
}

export function disputeStatus(d: StatementDispute): { label: string; tone: Tone } {
  if (d.state === "open") return { label: "Open: under review", tone: "warning" };
  if (d.outcome === "upheld") return { label: "Resolved: upheld", tone: "success" };
  return { label: "Resolved: denied", tone: "neutral" };
}

/**
 * What a resolution means for this party (0063 header "LEDGER MOVEMENT OF
 * AN UPHOLD"; UX-DESIGN.md §2.2, §3.6).
 */
export function resolutionText(r: DisputeResolution, party: PartyType): string {
  switch (r) {
    case "reversed":
      return party === "advertiser"
        ? "Upheld. The charge for this delivery was reversed and the amount returned to your wallet's spendable balance. The statement gets an amendment whose correction lines reverse the disputed line."
        : "Upheld. The delivery was reversed, so your share of it was taken back off your statement in an amendment.";
    case "already_reversed":
      return "Upheld. The delivery had already been reversed (for example by a safety finding) before the decision, so no further money moved.";
    case "credited":
      return "Upheld. A credit was posted against this delivery. It's recognized earnings you can include in your next payout, and it appears on an amendment of the statement you disputed.";
    case "manual_followup":
      return "Upheld, but this line isn't tied to a single delivery (a payout line, for example), so no credit could be posted automatically. VADS support will make the adjustment by hand; the reviewer's note below says what it will be.";
    case "denied":
      return "Denied. Nothing changed on your balance or statement. The reviewer's note below explains why. A line can be disputed only once.";
  }
}

/** The lifecycle strip: filed → under review → resolved. */
export function lifecycle(d: StatementDispute): Array<{ label: string; done: boolean; current: boolean }> {
  const resolved = d.state === "resolved";
  return [
    { label: "Filed", done: true, current: false },
    { label: "Under review", done: resolved, current: !resolved },
    { label: resolved ? (d.outcome === "upheld" ? "Upheld" : "Denied") : "Resolved", done: resolved, current: resolved },
  ];
}
